import React from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';

import Badge from '@decisiv/ui-components/lib/components/Badge';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3 } from '@decisiv/ui-components/lib/components/Typography';

import Building from '@decisiv/iconix/lib/components/Building';

import Card from 'components/DashboardCard';
import Loading from 'components/Loading';
import Divider from 'components/Divider';
import useSentinelTeam from 'hooks/useSentinelTeam';
import useTeamNavigate from 'hooks/useTeamNavigate';
import { useCurrentUserContext } from 'utils/User';

import { useAssignedToMe } from '../../AssignedToMeContext';
import TotalAssetGroupings from './TotalAssetGroupings';
import TotalAssignedToMe from './TotalAssignedToMe';

const totalAssetGroupingsQuery = loader('./totalAssetGrouping.graphql');
const totalAssignedToMeQuery = loader('./totalAssignedToMe.graphql');

function AssignedOnlyView({ count }) {
  const { isFleet } = useSentinelTeam();
  const navigate = useTeamNavigate();

  const navigateTo = isFleet
    ? '/depots?onlyAssignedToMe=true'
    : '/customers?onlyAssignedToMe=true';

  const label = isFleet ? t`Depots` : t`Customers`;
  const viewLabel = isFleet
    ? t`View Assigned Depots`
    : t`View Assigned Customers`;

  const displayCount = count === 0 ? '―' : count;

  return (
    <Flex flex={1} flexDirection="column">
      <Flex flexDirection="row" alignItems="center">
        <Badge
          variant="square"
          icon={Building}
          color={count === 0 ? 'licoriceMousse' : 'information'}
        />
        <Flex marginLeft={1} alignItems="center">
          <H3 weight="semibold">{displayCount}</H3>
          <H3
            weight="medium"
            css={`
              margin-left: 10px;
              white-space: nowrap;
            `}
          >
            {label}
          </H3>
        </Flex>
      </Flex>
      <Flex justifyContent="end">
        <Button
          aria-label={viewLabel}
          text={t`View`}
          variant="ghost"
          onClick={() => navigate(navigateTo)}
        />
      </Flex>
    </Flex>
  );
}

AssignedOnlyView.propTypes = {
  count: PropTypes.number.isRequired,
};

export default function TotalAssetGroupingsCard() {
  const { id } = useCurrentUserContext();
  const { isFleet } = useSentinelTeam();
  const { showOnlyAssignedToMe } = useAssignedToMe();

  const {
    loading: loadingAssetGroupings,
    data: {
      allAssetGroupings: {
        pagination: { totalEntries: assetGroupingsCount } = {},
      } = {},
    } = {},
  } = useQuery(totalAssetGroupingsQuery, {
    fetchPolicy: 'network-only',
    variables: { onlyAssignedToMe: showOnlyAssignedToMe || undefined },
  });

  const {
    loading: loadingAssignedToMe,
    data: {
      sentinelTeam: {
        assignees: {
          pagination: { totalEntries: assignedToMeCount } = {},
        } = {},
      } = {},
    } = {},
  } = useQuery(totalAssignedToMeQuery, {
    fetchPolicy: 'network-only',
    variables: { userId: id },
    skip: showOnlyAssignedToMe,
  });

  const headerText = isFleet ? t`Total Depots` : t`Total Customers`;

  const loadingMessage = isFleet
    ? t`Loading Depot Information...`
    : t`Loading Customer Information...`;

  const isLoading = showOnlyAssignedToMe
    ? loadingAssetGroupings
    : loadingAssetGroupings || loadingAssignedToMe;

  if (isLoading)
    return (
      <Card marginTop={0}>
        <Flex flexDirection="column" flex={1}>
          <Flex alignItems="center">
            <Badge variant="square" icon={Building} color="information" />
            <Flex marginLeft={1} alignItems="center">
              <H3
                weight="medium"
                css={`
                  white-space: nowrap;
                `}
              >
                {headerText}
              </H3>
            </Flex>
            <Loading inline imgDim={25}>
              {loadingMessage}
            </Loading>
          </Flex>
        </Flex>
      </Card>
    );

  if (showOnlyAssignedToMe) {
    return (
      <Card marginTop={0}>
        <Flex marginTop={1.25}>
          <AssignedOnlyView count={assetGroupingsCount} />
        </Flex>
      </Card>
    );
  }

  return (
    <Card marginTop={0}>
      <Flex marginTop={1.25}>
        <TotalAssetGroupings count={assetGroupingsCount} />
        <Divider
          css={`
            height: auto;
          `}
          marginX={1}
        />
        <TotalAssignedToMe count={assignedToMeCount} />
      </Flex>
    </Card>
  );
}
